// ── The company workspace's menu tree (owner 2026-10-13) ────────────────────────────────────────
// ONE constant enumerates every menu and submenu the workspace knows, and the URL's ?menu=&sub= are
// validated against it: embedded children (Bookkeeping, TaxView) read and write their own params on
// the SAME search string, so anything not listed here is ignored rather than trusted. Unknown values
// fall to Dashboard → Financial overview.
//
// The flags are what the shell decides on BEFORE a pane is mounted:
//   status        'ready' | 'partial' (something real is mounted) | 'placeholder' (not built yet —
//                 HIDDEN from the shell: no chip, not reachable from the URL. The owner's directive
//                 (2026-10-13) is no grey menus, so an entry waits here unseen until its batch flips
//                 it to 'ready'; see visibleSubs() / resolveMenu()). Since batch 4 (reimbursements)
//                 NO entry carries it: every submenu is built. The value stays in the type and the
//                 filter stays in visibleSubs() so a future menu can be staged the same way, hidden.
//   needsPeriod   the pane cannot work without an accounting period → the "open the first period" card
//   modules       every key must be in me.modules, else the "needs permission" card — the API behind
//                 those screens is gated by requireModule on the server and would 403 anyway
//   needsMerchant the pane reads the client company's own portal documents → the "no portal account"
//                 card when /summary says hasMerchant=false
//   thaiOnly      the pane lists Thai tax documents (tax invoices, credit/debit notes): for a merchant
//                 that is NOT in Thai tax mode they do not exist, so the entry is HIDDEN exactly like a
//                 placeholder — no muted chip, no "does not apply" card (owner 2026-10-13: no grey menus;
//                 reviewer 2026-10-13). Unknown yet (/summary still loading) = listed; the pane fetches
//                 nothing in that case and the shell re-resolves the URL when the summary arrives.
export type AwStatus = 'ready' | 'partial' | 'placeholder';
export type AwSub = {
  key: string;
  labelKey: string;
  status: AwStatus;
  needsPeriod?: boolean;
  modules?: string[];
  needsMerchant?: boolean;
  thaiOnly?: boolean;
};
export type AwMenu = { key: string; labelKey: string; icon: string; subs: AwSub[] };

export const WORKSPACE_MENUS: AwMenu[] = [
  { key: 'dashboard', labelKey: 'aw_menu_dashboard', icon: 'ti ti-layout-dashboard', subs: [
    { key: 'overview', labelKey: 'aw_sub_overview', status: 'ready', needsPeriod: true },
    { key: 'ar_ap', labelKey: 'aw_sub_ar_ap', status: 'partial', needsMerchant: true },
    { key: 'cash_flow', labelKey: 'aw_sub_cash_flow', status: 'ready', needsPeriod: true },
    { key: 'tax_deadlines', labelKey: 'aw_sub_tax_deadlines', status: 'ready' },
  ] },
  // Sales = the CLIENT COMPANY'S OWN documents (its invoicing portal), never the firm's bills to it.
  { key: 'sales', labelKey: 'aw_menu_sales', icon: 'ti ti-file-invoice', subs: [
    { key: 'quotations', labelKey: 'aw_sub_quotations', status: 'partial', needsMerchant: true },
    { key: 'invoices', labelKey: 'aw_sub_invoices', status: 'partial', needsMerchant: true },
    { key: 'tax_invoices', labelKey: 'aw_sub_tax_invoices', status: 'partial', needsMerchant: true, thaiOnly: true },
    { key: 'receipts', labelKey: 'aw_sub_receipts', status: 'partial', needsMerchant: true },
    { key: 'credit_notes', labelKey: 'aw_sub_credit_notes', status: 'partial', needsMerchant: true, thaiOnly: true },
  ] },
  // Purchase orders, payables (Payments) and purchase debit/credit notes are REGISTERS of the client
  // company (merchant_purchase_orders / merchant_payables / merchant_purchase_notes keyed by its
  // merchant row, batch 3): they need the merchant row like Sales does, and the registers take the
  // document tables' width. Nothing in them posts to the statutory ledger.
  { key: 'purchases', labelKey: 'aw_menu_purchases', icon: 'ti ti-shopping-cart', subs: [
    { key: 'purchase_orders', labelKey: 'aw_sub_purchase_orders', status: 'ready', needsMerchant: true },
    { key: 'supplier_bills', labelKey: 'aw_sub_supplier_bills', status: 'partial', needsMerchant: true },
    { key: 'debit_credit_notes', labelKey: 'aw_sub_debit_credit_notes', status: 'ready', needsMerchant: true },
  ] },
  { key: 'expenses', labelKey: 'aw_menu_expenses', icon: 'ti ti-receipt', subs: [
    { key: 'expense_records', labelKey: 'aw_sub_expense_records', status: 'partial', modules: ['bookkeeping'] },
    // Categories = the company's own expense accounts in its chart, totalled per period by the P&L's
    // rule on the server (/acct-workspace/expense-categories) — never the firm-wide expense table.
    { key: 'expense_categories', labelKey: 'aw_sub_expense_categories', status: 'ready', needsPeriod: true },
    // Reimbursements = a register of the client company (merchant_reimbursements keyed by its merchant
    // row, batch 4): claims submitted → approved / rejected → paid, receipts per line, paying from a
    // statement line only marks that line. Needs the merchant row like Sales; the claims table takes
    // the document tables' width. This was the last 'placeholder' entry — none remains (owner 2026-10-13).
    { key: 'reimbursements', labelKey: 'aw_sub_reimbursements', status: 'ready', needsMerchant: true },
    // Payments = the client company's accounts-payable register (merchant_payables and their
    // payments). It sat under Purchases until the owner moved it here (2026-10-13): what a company
    // pays out is one subject, and Purchases keeps the ordering side (orders, bills, notes).
    { key: 'payments', labelKey: 'aw_sub_payments', status: 'ready', needsMerchant: true },
  ] },
  // The firm's own bank modules (bank_acc / bank_legal) are the FIRM's money and never appear here.
  // Statements, lines, reconciliation and transfers are the CLIENT COMPANY's own accounts
  // (merchant_bank_statements / merchant_bank_lines keyed by its merchant row), so the three need the
  // merchant row like Sales does; the lines table takes the document tables' width.
  { key: 'banking', labelKey: 'aw_menu_banking', icon: 'ti ti-building-bank', subs: [
    { key: 'bank_accounts', labelKey: 'aw_sub_bank_accounts', status: 'partial' },
    { key: 'transactions', labelKey: 'aw_sub_transactions', status: 'ready', needsMerchant: true },
    { key: 'reconciliation', labelKey: 'aw_sub_reconciliation', status: 'ready', needsMerchant: true },
    { key: 'transfers', labelKey: 'aw_sub_transfers', status: 'ready', needsMerchant: true },
  ] },
  { key: 'accounting', labelKey: 'aw_menu_accounting', icon: 'ti ti-book-2', subs: [
    { key: 'chart', labelKey: 'aw_sub_chart', status: 'ready' },
    { key: 'journal_entries', labelKey: 'aw_sub_journal_entries', status: 'ready', needsPeriod: true },
    { key: 'general_ledger', labelKey: 'aw_sub_general_ledger', status: 'ready', needsPeriod: true },
    { key: 'trial_balance', labelKey: 'aw_sub_trial_balance', status: 'ready', needsPeriod: true },
    { key: 'periods', labelKey: 'aw_sub_periods', status: 'ready', needsPeriod: true },
    { key: 'closing', labelKey: 'aw_sub_closing', status: 'ready', needsPeriod: true },
  ] },
  // VAT / WHT / PP30 / PND live in the Tax module (module key 'tax'); only Tax reports is ours.
  { key: 'tax', labelKey: 'aw_menu_tax', icon: 'ti ti-receipt-tax', subs: [
    // VAT and WHT used to sit in front of these two and showed the same thing twice: VAT mounted
    // TaxView on initialView 'vat', which is exactly what ภ.พ.30 mounts, and WHT offered ภ.ง.ด.53 /
    // ภ.ง.ด.3, a subset of what ภ.ง.ด. offers. The owner removed both (2026-10-13); nothing was lost,
    // and WHT's caption moved into PndPane so its one piece of guidance survived.
    { key: 'pp30', labelKey: 'aw_sub_pp30', status: 'partial', modules: ['tax'] },
    { key: 'pnd', labelKey: 'aw_sub_pnd', status: 'partial', modules: ['tax'] },
    { key: 'tax_reports', labelKey: 'aw_sub_tax_reports', status: 'partial', needsPeriod: true },
  ] },
  { key: 'reports', labelKey: 'aw_menu_reports', icon: 'ti ti-report-analytics', subs: [
    { key: 'pl', labelKey: 'aw_sub_pl', status: 'ready', needsPeriod: true },
    { key: 'balance_sheet', labelKey: 'aw_sub_balance_sheet', status: 'ready', needsPeriod: true },
    { key: 'cash_flow', labelKey: 'aw_sub_cash_flow', status: 'ready', needsPeriod: true },
    // Aging = the dashboard's receivables predicate bucketed by days past due on the server
    // (/acct-workspace/aging); it reads the company's portal documents, hence needsMerchant, and the
    // per-currency cards plus the rows table take the document tables' width.
    { key: 'ar_ap_aging', labelKey: 'aw_sub_ar_ap_aging', status: 'ready', needsMerchant: true },
  ] },
  { key: 'settings', labelKey: 'aw_menu_settings', icon: 'ti ti-settings', subs: [
    { key: 'company_profile', labelKey: 'aw_sub_company_profile', status: 'ready' },
    { key: 'tax_settings', labelKey: 'aw_sub_tax_settings', status: 'partial' },
    { key: 'document_numbering', labelKey: 'aw_sub_document_numbering', status: 'partial', needsMerchant: true },
    { key: 'users_permissions', labelKey: 'aw_sub_users_permissions', status: 'partial' },
  ] },
];

export const DEFAULT_MENU = 'dashboard';
export const DEFAULT_SUB = 'overview';

/** What /summary has told the shell about THIS company; undefined = not known yet. */
export type AwVisibility = { thaiTaxMode?: boolean };
/** The submenus the shell shows for this company: everything that is built, minus what does not apply to
 *  it. A 'placeholder' entry is kept in the tree for the batch that will build it but is neither listed nor
 *  reachable (no grey menu, owner 2026-10-13); as of batch 4 nothing is left to hide that way — the filter
 *  is a standing rule, not a current need. A thaiOnly entry is hidden the same way once /summary says the
 *  merchant is not in Thai tax mode (thaiTaxMode === false); while that is unknown it stays listed. */
export function visibleSubs(menu: AwMenu, vis: AwVisibility = {}): AwSub[] {
  const v = menu.subs.filter(s => s.status !== 'placeholder' && !(s.thaiOnly && vis.thaiTaxMode === false));
  return v.length ? v : menu.subs.slice(0, 1);   // a menu with nothing built yet still needs one entry to stand on
}
/** The menu/sub the URL asks for, or the default pair when either is unknown, not yet built or not applicable. */
export function resolveMenu(menuKey: string | null, subKey: string | null, vis: AwVisibility = {}): { menu: AwMenu; sub: AwSub } {
  const menu = WORKSPACE_MENUS.find(m => m.key === menuKey) || WORKSPACE_MENUS.find(m => m.key === DEFAULT_MENU)!;
  const subs = visibleSubs(menu, vis);
  const sub = subs.find(s => s.key === subKey) || subs[0];
  return { menu, sub };
}
